<template>
  <ol
    class="appointment-report ar-stepper"
    :class="`ar-stepper--${size}`"
    :aria-label="A11Y_COPY.steps"
  >
    <li
      v-for="step in items"
      :key="step.index"
      class="ar-stepper__step"
      :class="`ar-stepper__step--${step.state}`"
      :aria-current="step.current ? 'step' : null"
    >
      <span
        class="ar-stepper__marker"
        :class="{ 'ar-anim-fade-in': step.state === 'done' && step.index === current }"
        aria-hidden="true"
      >
        <feather-icon
          v-if="step.state === 'done'"
          icon="CheckIcon"
          :size="iconSizes.done"
        />
        <feather-icon
          v-else-if="step.state === 'locked' || step.showLock"
          icon="LockIcon"
          :size="iconSizes.lock"
        />
        <feather-icon
          v-else-if="step.icon"
          :icon="step.icon"
          :size="iconSizes.step"
        />
        <template v-else>{{ step.index + 1 }}</template>
      </span>
      <span class="ar-stepper__text">
        <span class="ar-stepper__label">
          {{ step.label }}<span
            v-if="step.srSuffix"
            class="ar-sr-only"
          > {{ step.srSuffix }}</span>
        </span>
        <span
          v-if="step.caption"
          class="ar-stepper__caption ar-tabular"
        >{{ step.caption }}</span>
      </span>
      <span
        v-if="!step.last"
        class="ar-stepper__connector"
        :class="{ 'ar-stepper__connector--reached': step.index < current }"
        aria-hidden="true"
      />
    </li>
  </ol>
</template>

<script>
import { A11Y_COPY, NEW_COPY, STEPPER_STEPS } from '@/constants/appointment_report_v2'

const CURRENT_STATES = ['active', 'locked', 'done']

/**
 * Stepper compacto do Figma: "Novo compromisso" → "Report de compromisso".
 * Etapas antes de `current` = concluídas (check); a etapa `current` fica `state` (active: número em laranja;
 * locked: cadeado — aguardando o horário; done: check — MX Salvo); as seguintes ficam neutras.
 *
 *   Report: <report-stepper :current="1" :state="aguardando ? 'locked' : (salvo ? 'done' : 'active')" />
 *   Novo compromisso: <report-stepper :current="0" lock-upcoming :captions="['', NEW_COPY.reportAvailableAtMeeting]" />
 *
 * `captions` = uma 2ª linha opcional por etapa (12/16 500, muted); `lock-upcoming` mostra o cadeado nas etapas
 * seguintes (o report só abre no horário da reunião).
 *
 * `size="lg"` + `icons` = o stepper do board v4-novo-compromisso: marcadores de 28px, rótulos 12/16 600, conector de
 * 40px, etapa futura em superfície elevada e, na etapa atual, o ícone da etapa (ex. `['FilePlusIcon']`) no lugar do
 * número. O padrão (`md`) é o do board v4-report.
 */
export default {
  name: 'ReportStepper',
  props: {
    current: {
      type: Number,
      default: 1,
    },
    state: {
      type: String,
      default: 'active',
      validator: value => CURRENT_STATES.includes(value),
    },
    steps: {
      type: Array,
      default: () => STEPPER_STEPS,
    },
    captions: {
      type: Array,
      default: () => [],
    },
    lockUpcoming: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['md', 'lg'].includes(value),
    },
    // Ícone feather por etapa, mostrado na etapa atual em andamento (`active`) no lugar do número.
    icons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      A11Y_COPY,
    }
  },
  computed: {
    iconSizes() {
      return this.size === 'lg'
        ? { done: '16', lock: '14', step: '16' }
        : { done: '14', lock: '12', step: '14' }
    },
    items() {
      return this.steps.map((label, index) => {
        let state = 'upcoming'
        if (index < this.current) state = 'done'
        else if (index === this.current) state = this.state
        let srSuffix = ''
        if (state === 'done') srSuffix = NEW_COPY.stepDone
        else if (state === 'locked') srSuffix = NEW_COPY.stepLocked

        return {
          index,
          label,
          state,
          srSuffix,
          caption: this.captions[index] || '',
          icon: state === 'active' ? this.icons[index] || '' : '',
          showLock: state === 'upcoming' && this.lockUpcoming,
          current: index === this.current && state !== 'done',
          last: index === this.steps.length - 1,
        }
      })
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-stepper {
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  list-style: none;
}

.ar-stepper__step {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
  white-space: nowrap;
}

.ar-stepper__marker {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-pill);
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
}

.ar-stepper__step--done .ar-stepper__marker {
  background: var(--ar-selected-tint);
  color: var(--ar-brand-text);
}

.ar-stepper__step--active {
  font-weight: 600;
  color: var(--ar-text-1);

  .ar-stepper__marker {
    background: var(--ar-brand);
    color: var(--ar-brand-ink);
  }
}

.ar-stepper__step--locked .ar-stepper__marker,
.ar-stepper__step--upcoming .ar-stepper__marker {
  background: var(--ar-bg-field);
  border: 1px solid var(--ar-control);
  color: var(--ar-text-2);
}

// Aguardando o horário: a etapa atual continua em destaque de peso, com o texto em tom secundário.
.ar-stepper__step--locked {
  font-weight: 600;
}

.ar-stepper__step--upcoming {
  color: var(--ar-text-muted);
}

.ar-stepper__text {
  display: flex;
  flex-direction: column;
}

.ar-stepper__caption {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
}

.ar-stepper__connector {
  width: 32px;
  height: 2px;
  margin-left: 2px;
  border-radius: 2px;
  background: var(--ar-divider);
  transition: background-color var(--ar-dur-progress) var(--ar-ease-standard);
}

.ar-stepper__connector--reached {
  background: var(--ar-brand);
}

// ---- lg: board v4-novo-compromisso ----
.ar-stepper--lg {
  gap: 12px;

  .ar-stepper__step {
    gap: 10px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
  }

  .ar-stepper__marker {
    width: 28px;
    height: 28px;
  }

  .ar-stepper__step--locked .ar-stepper__marker,
  .ar-stepper__step--upcoming .ar-stepper__marker {
    background: var(--ar-bg-raised);
  }

  .ar-stepper__step--upcoming {
    color: var(--ar-text-2);
  }

  .ar-stepper__connector {
    width: 40px;
  }
}
</style>
